import { ShownPeriodsData } from 'src/app/domain/financial-statement/utils/common/utilityClasses';
import { Rows } from '../../../interfaces';
import { formats, getCell, withCellStyles } from '../../../utils';
import { PlExcelExportData } from './profitAndLossTemplate';
import { FinancialStatementPart } from 'src/app/domain/financial-statement/models/common/FinancialStatementPart';
import { boldGrayBgLeft, boldLightGrayBgLeft, leftBold } from 'src/app/util/excelStyles';
import { FinancialStatementItem } from 'src/app/domain/financial-statement/models/common/FinancialStatementItem';
import { intToExcelCol } from 'excel-column-name';
import { dashInsteadOfNull } from 'src/app/services/excel/templates/pp-by-fund/mapping';
import { PL_TABS } from '../../../../financial-statement/component/pl-page/pl-page.component';

export const getPreparedData = (data: PlExcelExportData) => {
  const newData: Rows = [];
  const headerRow = [];
  const type = data.periodType;
  totalsIndexArray = [];
  const headersForDisplay = generateHeadersForDisplay(data.shownPeriodsData, data.selectedPeriod, type);
  headersForDisplay.forEach((header) => headerRow.push(withCellStyles(header, leftBold)));
  const headersForLogic = getHeadersForLogic(headersForDisplay, data.shownPeriodsData, type);
  newData.push(headerRow);
  currentIndex++;
  data.parts.forEach((part) => {
    const partRow = getPartRow(part, headersForLogic);
    newData.push(...partRow);
    currentIndex += 1;
    const itemsRows = getItemRows(part, headersForLogic, type);
    newData.push(...itemsRows);
    currentIndex += itemsRows.length;
    
    const subTotalRow = getSubTotalRow(part, headersForLogic);
    if (subTotalRow) {
      newData.push(...subTotalRow);
      currentIndex += 1;
    }
 

  });
  const grandTotalRow = getGrandTotalRow(headersForLogic);
  newData.push(...grandTotalRow);
  currentIndex = 1;
  return newData;
};

export const generateHeadersForDisplay = (shownPeriodsData: ShownPeriodsData, period: Date, type: typeof PL_TABS[number]) => {
  const headersForDisplay: string[] = [''];
  const headers = type === PL_TABS[0] ? shownPeriodsData.quarterHeaders : shownPeriodsData.yearHeaders;
  headers.forEach((el) => {
    const header = el.start + ' \ntill ' + el.end;
    headersForDisplay.push(header);
  });
  headersForDisplay.push('Inception \ntill ' + period);
  return headersForDisplay;
};

export const getHeadersForLogic = (headersForDisplay: string[], shownPeriodsData: ShownPeriodsData, type: typeof PL_TABS[number]) => {
  const len = headersForDisplay.length;
  const headers = type === PL_TABS[0] ? shownPeriodsData.quarters : shownPeriodsData.years;
  const headersForLogic = [];
  headersForLogic.push('name');
  headersForLogic.push(...headers);
  headersForLogic.push('Inception till');
  return headersForLogic;
};

export const getPartRow = (part: FinancialStatementPart, headersForLogic: string[]) => {
  const rows: Rows = [];
  const row = [];
  const partMapping = getPartMapping(part.partName, headersForLogic);
  for (const header of headersForLogic) {
    row.push(withCellStyles(partMapping[header], leftBold));
  }
  rows.push(row);
  return rows;
};

export const getPartMapping = (partName: string, headersForLogic: string[]) => {
  const body = {};
  headersForLogic.forEach((header, i) => {
    if (i === 0) {
      body[header] = partName;
    } else {
      body[header] = '';
    }
  });

  return body;
};


export const getItemRows = (part: FinancialStatementPart, headersForLogic: string[], type: typeof PL_TABS[number]) => {
  const rows: Rows = [];
  const items = part.financialStatementItems.filter((it) => it.financialStatementAccounts?.length);
  items.forEach((item) => {
    const row = [];
    const itemMapping = getItemMapping(item, headersForLogic, type);
    for (const header of headersForLogic) {
      row.push(itemMapping[header]);
    }
    rows.push(row);
  });
  return rows;
};

export const getItemMapping = (item: FinancialStatementItem, headersForLogic: string[], type: typeof PL_TABS[number]) => {
  const body = {};
  const yearType = type === PL_TABS[1];
  headersForLogic.forEach((header, i) => {
    if (i === 0) {
      body[header] = item.name.toUpperCase();
    } else if (i === headersForLogic.length - 1) {
      body[header] = getCell({
        v: dashInsteadOfNull(-item.sums.balance),
        z: formats.thousandsSeparatorToFixed2,
        t: 'n',
      });
    } else {
      body[header] = getCell({
        v: dashInsteadOfNull(yearType ? -item.sums.yearlySums.get(header) : -item.sums.quarterlySums.get(header)),
        z: formats.thousandsSeparatorToFixed2,
        t: 'n',
      });
    }

  });
  return body;
};

export const getSubTotalRow = (part: FinancialStatementPart, headersForLogic: string[]) => {
  const rows: Rows = [];
  const row = [];
  const subTotalMapping = getSubTotalMapping(part, headersForLogic);
  if (!subTotalMapping) {
    return null;
  }
  for (const header of headersForLogic) {
    row.push(withCellStyles(subTotalMapping[header], boldLightGrayBgLeft));
  }
  rows.push(row);
  return rows;
};

export const getSubTotalMapping = (part: FinancialStatementPart, headersForLogic: string[]) => {
  const subTotal = {};
  const itemLen = part.financialStatementItems.filter((it) => it.financialStatementAccounts?.length).length;
  if (!itemLen) {
    return null;
  }
  totalsIndexArray.push(currentIndex);
  headersForLogic.forEach((header, rowIndex) => {
    // check if it is 1st element
    if (rowIndex === 0) {
      subTotal[header] = 'Total ' + part.partName;
      // check if it last element
    } else if (rowIndex === headersForLogic.length - 1) {
      // current index is on total row so needs to define start and stop position
      // currentIndex - itemLen -> start position
      // currentIndex  - 1 stop position cause we know that stop line is one before total
      subTotal[header] = getCell({
        f: getFormulasForSubTotal(currentIndex, itemLen, rowIndex, true),
        z: formats.thousandsSeparatorToFixed2,
        t: 'n',
      });
    } else {
      subTotal[header] = getCell({
        f: getFormulasForSubTotal(currentIndex, itemLen, rowIndex, false),
        z: formats.thousandsSeparatorToFixed2,
        t: 'n',
      });
    }

  });
  return subTotal;
};

export const getFormulasForSubTotal = (curIndex: number, itemLen: number, rowIndex: number, isLastElement: boolean) => (isLastElement ?
  `SUM(${intToExcelCol(rowIndex + 1)}${curIndex - itemLen}:${intToExcelCol(rowIndex + 1)}${curIndex - 1})` :
  `SUM(${intToExcelCol(rowIndex + 1)}${curIndex - 1}: ${intToExcelCol(rowIndex + 1)}${curIndex - itemLen})`);

export const getGrandTotalRow = (headersForLogic: string[]) => {
  const rows: Rows = [];
  const row = [];
  const grandTotalMapping = getGrandTotalMapping(headersForLogic);
  for (const header of headersForLogic) {
    row.push(withCellStyles(grandTotalMapping[header], boldGrayBgLeft));
  }
  rows.push(row);
  return rows;
};

export const getGrandTotalMapping = (headersForLogic: string[]) => {
  const grandTotal = {};
  headersForLogic.forEach((header, rowIndex) => {
    if (rowIndex === 0) {
      grandTotal[header] = 'Grand total';
    } else {
      grandTotal[header] = getCell({
        f: getGrandTotalSum(rowIndex + 1),
        z: formats.thousandsSeparatorToFixed2,
        t: 'n',
      });
    }

  });

  return grandTotal;
};

export const getGrandTotalSum = (rowIndex: number) => {
  const letter = intToExcelCol(rowIndex);
  const result = totalsIndexArray.map((totalI) => `${letter}${totalI}`).join('+');
  return result;
};

export let currentIndex = 1;

export let totalsIndexArray = [];
